'use client';

import { useEffect, useRef, useState } from 'react';
import { ArticleCardClient, ClientArticle } from './ArticleCardClient';

type Props = {
  apiUrl: string; // e.g., /api/articles?context=all or ...&q=...
  startPage: number; // usually currentPage + 1
  hasMoreInitial: boolean;
};

export const InfiniteArticles = ({ apiUrl, startPage, hasMoreInitial }: Props) => {
  const [page, setPage] = useState(startPage);
  const [items, setItems] = useState<ClientArticle[]>([]);
  const [hasMore, setHasMore] = useState(hasMoreInitial);
  const [loading, setLoading] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const storageKey = `infinite:${apiUrl}`;

  // Restore from sessionStorage on mount
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const saved = JSON.parse(raw) as {
          items: ClientArticle[];
          page: number;
          hasMore: boolean;
          scrollY?: number;
        };
        setItems(saved.items || []);
        setPage(saved.page || startPage);
        setHasMore(typeof saved.hasMore === 'boolean' ? saved.hasMore : hasMoreInitial);

        // Restore scroll after paint
        const y = saved.scrollY ?? 0;
        if (y > 0) {
          requestAnimationFrame(() => {
            window.scrollTo(0, y);
          });
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist to sessionStorage on changes
  useEffect(() => {
    try {
      const payload = JSON.stringify({ items, page, hasMore, scrollY: window.scrollY });
      sessionStorage.setItem(storageKey, payload);
    } catch {}
  }, [items, page, hasMore, storageKey]);

  // Track scroll position in storage (throttled via rAF)
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          try {
            const raw = sessionStorage.getItem(storageKey);
            const data = raw ? JSON.parse(raw) : {};
            data.scrollY = window.scrollY;
            sessionStorage.setItem(storageKey, JSON.stringify(data));
          } catch {}
          ticking = false;
        });
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [storageKey]);

  useEffect(() => {
    if (!hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;

    const onIntersect: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !loading && hasMore) {
          void loadMore();
        }
      });
    };

    const obs = new IntersectionObserver(onIntersect, { rootMargin: '200px' });
    obs.observe(el);
    return () => {
      obs.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, sentinelRef.current]);

  const loadMore = async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    try {
      const sep = apiUrl.includes('?') ? '&' : '?';
      const res = await fetch(`${apiUrl}${sep}page=${page}`, { cache: 'no-store' });
      if (!res.ok) throw new Error('Failed to load');
      const json = await res.json();
      const newItems: ClientArticle[] = json.data || [];
      setItems((prev) => [...prev, ...newItems]);
      setHasMore(Boolean(json.hasMore));
      setPage((p) => p + 1);
    } catch (e) {
      // swallow errors to avoid breaking UX
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {items.map((article) => (
        <ArticleCardClient key={article.id} article={article} href={`/articles/${article.id}`} target="_blank" />
      ))}
      {hasMore ? (
        <div ref={sentinelRef} className="w-full flex items-center justify-center py-8 text-default-500">
          {loading ? 'Завантаження…' : 'Гортайте вниз для завантаження ще'}
        </div>
      ) : null}
    </>
  );
};
